/** One word of the manifesto body (punctuation stays attached). */
export interface BodyWord {
  text: string
  /** Whether whitespace precedes it in the source (false for "." straight after a phrase). */
  space: boolean
  /** BCP 47 code for a non-English word, e.g. `hi-Latn` for "kartā". */
  lang?: string
}

/** A run of words: plain, or one of `manifesto.highlights`. */
export interface BodyRun {
  words: BodyWord[]
  /** Index into the highlights array, or `null` for plain text. */
  highlight: number | null
}

const EDGES = /^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu

function toWords(
  text: string,
  langWords: ReadonlyMap<string, string>,
  leadingSpace: boolean,
): BodyWord[] {
  const words: BodyWord[] = []
  for (const match of text.matchAll(/(\s*)(\S+)/g)) {
    const [, gap, word] = match
    const space = words.length === 0 ? leadingSpace || gap.length > 0 : gap.length > 0
    const lang = langWords.get(word.replace(EDGES, ''))
    words.push(lang ? { text: word, space, lang } : { text: word, space })
  }
  return words
}

/**
 * Splits the manifesto body into runs of words, marking each highlight phrase (exact
 * substrings, matched in order) and tagging known foreign words with their language.
 * A highlight that isn't found is skipped, so a copy edit can't break the section.
 */
export function tokenizeBody(
  body: string,
  highlights: readonly string[],
  langWords: ReadonlyMap<string, string> = new Map(),
): BodyRun[] {
  const runs: BodyRun[] = []
  let cursor = 0
  const push = (text: string, highlight: number | null, start: number) => {
    const leadingSpace = start > 0 && /\s/.test(body[start - 1] ?? '')
    const words = toWords(text, langWords, leadingSpace)
    if (words.length) runs.push({ words, highlight })
  }
  highlights.forEach((phrase, index) => {
    const at = phrase ? body.indexOf(phrase, cursor) : -1
    if (at === -1) return
    push(body.slice(cursor, at), null, cursor)
    push(phrase, index, at)
    cursor = at + phrase.length
  })
  push(body.slice(cursor), null, cursor)
  return runs
}
